import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Autocomplete,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    FormControlLabel,
    Switch,
    Card,
    CardContent,
    LinearProgress,
    Menu,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Inbox as InboxIcon,
    Cancel as CancelIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    FilterAlt as FilterIcon,
    Warning as WarningIcon,
    AutoFixHigh as MagicIcon,
    Refresh as RefreshIcon,
    Help as HelpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button,
    Divider,
    Stack
} from '@mui/material';
import DuplicateResolutionModal from './DuplicateResolutionModal';

const WebsiteScreensPreview = ({ site, weekDays, getSignedUrl }) => {
    const [urls, setUrls] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const loadUrls = async () => {
            const tempUrls = {};
            const promises = weekDays.map(async (day) => {
                if (!day.isValid()) return;
                const dayStr = day.format('YYYY-MM-DD');
                const capture = site.dailyStatus?.[dayStr];
                const imgLoc = capture?.ImageLocation;
                if (imgLoc) {
                    try {
                        const url = await getSignedUrl(imgLoc);
                        tempUrls[dayStr] = url;
                    } catch (e) {
                        console.error('Failed to get signed URL for preview:', e);
                    }
                }
            });
            await Promise.all(promises);
            if (isMounted) {
                setUrls(tempUrls);
                setLoading(false);
            }
        };
        loadUrls();
        return () => { isMounted = false; };
    }, [site, weekDays, getSignedUrl]);

    if (loading) {
        return (
            <TableCell colSpan={weekDays.length} sx={{ py: 1, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="textSecondary">Loading screenshot previews...</Typography>
                </Box>
            </TableCell>
        );
    }

    const captures = weekDays.filter(day => {
        if (!day.isValid()) return false;
        const dayStr = day.format('YYYY-MM-DD');
        return !!site.dailyStatus?.[dayStr]?.ImageLocation;
    });

    if (captures.length === 0) {
        return (
            <TableCell colSpan={weekDays.length} sx={{ py: 1, px: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    No screenshots captured for this date range.
                </Typography>
            </TableCell>
        );
    }

    return (
        <>
            {weekDays.map((day, idx) => {
                if (!day.isValid()) {
                    return <TableCell key={`preview-empty-${idx}`} sx={{ p: 0.5 }} />;
                }
                const dayStr = day.format('YYYY-MM-DD');
                const url = urls[dayStr];
                const capture = site.dailyStatus?.[dayStr];
                const imgLoc = capture?.ImageLocation;

                if (!imgLoc || !url) {
                    return <TableCell key={`preview-empty-${dayStr}`} sx={{ p: 0.5 }} />;
                }

                return (
                    <TableCell key={dayStr} align="center" sx={{ p: 0.5, verticalAlign: 'top' }}>
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', width: '100%' }}>
                            <Tooltip title="Click to open full screenshot in new tab">
                                <Box 
                                    component="img"
                                    src={url}
                                    alt={`${site.WebsiteName} ${dayStr}`}
                                    onClick={() => window.open(url, '_blank')}
                                    sx={{ 
                                        width: '100%', 
                                        maxWidth: 120, 
                                        height: 'auto', 
                                        maxHeight: 240,
                                        objectFit: 'contain', 
                                        borderRadius: 1, 
                                        border: '1px solid #ddd',
                                        cursor: 'pointer',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            transform: 'scale(1.1)',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                            zIndex: 1
                                        }
                                    }}
                                />
                            </Tooltip>
                            {capture.Checked && (
                                <Typography variant="caption" sx={{ color: 'success.main', display: 'block', fontSize: '0.65rem', fontWeight: 'medium' }}>
                                    Verified
                                </Typography>
                            )}
                        </Box>
                    </TableCell>
                );
            })}
        </>
    );
};

const AuditOverview = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ websites: [], filters: {} });
    
    // Filters
    const [startDate, setStartDate] = useState(moment.utc().startOf('week').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment.utc().startOf('week').add(6, 'days').format('YYYY-MM-DD'));
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [selectedCountry, setSelectedCountry] = useState('all');
    const [selectedTag, setSelectedTag] = useState('all');
    const [showOnlyUnchecked, setShowOnlyUnchecked] = useState(false);
    const [patchingMode, setPatchingMode] = useState(false);
    const [patchingIntelligence, setPatchingIntelligence] = useState({});
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    
    // Patching Modal State
    const [patchModalOpen, setPatchModalOpen] = useState(false);
    const [selectedPatch, setSelectedPatch] = useState(null);
    const [selectedPatchCandidate, setSelectedPatchCandidate] = useState(null);
    const [executingPatch, setExecutingPatch] = useState(false);
    const [patchPreviewUrl, setPatchPreviewUrl] = useState('');
    const [targetPreviewUrl, setTargetPreviewUrl] = useState('');

    // Duplicate Resolution Modal State
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [duplicateModalTarget, setDuplicateModalTarget] = useState({ site: null, date: null });

    const openDuplicateModal = (e, site, day) => {
        e.preventDefault();
        e.stopPropagation();
        setDuplicateModalTarget({ site, date: day });
        setDuplicateModalOpen(true);
    };

    // Collapsible Row Previews
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (siteId) => {
        setExpandedRows(prev => ({
            ...prev,
            [siteId]: !prev[siteId]
        }));
    };

    // Cell action menu state (in patching mode)
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [selectedMenuCell, setSelectedMenuCell] = useState(null); // { site, day, intel }

    // What's New Modal State
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    const [whatsNewLang, setWhatsNewLang] = useState('en');

    useEffect(() => {
        const seen = localStorage.getItem('whats_new_seen_audit_v1');
        if (!seen) {
            setWhatsNewOpen(true);
        }
    }, []);

    const handleCloseWhatsNew = () => {
        setWhatsNewOpen(false);
    };

    const handleDontShowWhatsNew = () => {
        localStorage.setItem('whats_new_seen_audit_v1', 'true');
        setWhatsNewOpen(false);
    };

    const handleStartDateChange = (newStart) => {
        setStartDate(newStart);
        if (newStart) {
            setEndDate(moment.utc(newStart).add(6, 'days').format('YYYY-MM-DD'));
        }
    };

    const handleShiftDays = (days) => {
        setStartDate(prev => moment.utc(prev).add(days, 'days').format('YYYY-MM-DD'));
        setEndDate(prev => moment.utc(prev).add(days, 'days').format('YYYY-MM-DD'));
    };

    // Dynamic Week Days (UTC)
    const weekDays = useMemo(() => {
        const days = [];
        const mStart = moment.utc(startDate, 'YYYY-MM-DD', true);
        const mEnd = moment.utc(endDate, 'YYYY-MM-DD', true);
        const validStart = mStart.isValid() ? mStart : moment.utc().startOf('week');
        const validEnd = mEnd.isValid() && !mEnd.isBefore(validStart)
            ? mEnd
            : moment.utc(validStart).add(6, 'days');
        
        let curr = moment.utc(validStart);
        while (!curr.isAfter(validEnd)) {
            days.push(moment.utc(curr));
            curr.add(1, 'days');
        }
        return days;
    }, [startDate, endDate]);

    const fetchAuditSummary = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/summary`, {
                params: {
                    startDate: moment.utc(startDate).startOf('day').toISOString(),
                    endDate: moment.utc(endDate).endOf('day').toISOString()
                },
                headers: {
                    'x-api-key': process.env.REACT_APP_API_KEY
                }
            });
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch audit summary:', error);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);
    
    const handleRefresh = async () => {
        if (patchingMode) {
            setPatchingIntelligence({});
            inFlightRequests.current.clear();
            setRefreshTrigger(prev => prev + 1);
        }
        await fetchAuditSummary();
    };

    useEffect(() => {
        fetchAuditSummary();
    }, [fetchAuditSummary]);

    // Filter Logic
    const filteredWebsites = useMemo(() => {
        if (!data.websites) return [];
        
        return data.websites.filter(site => {
            const matchesRegion = selectedRegion === 'all' || site.Region === selectedRegion;
            const matchesCountry = selectedCountry === 'all' || site.Country === selectedCountry;
            const matchesTag = selectedTag === 'all' || (site.Tags && site.Tags.includes(selectedTag));
            
            if (!matchesRegion || !matchesCountry || !matchesTag) return false;
            
            if (showOnlyUnchecked) {
                // Check if any day in the range is unchecked but has a capture
                const days = Object.values(site.dailyStatus);
                const allChecked = days.length > 0 && days.every(d => d.Checked);
                return !allChecked;
            }
            
            return true;
        });
    }, [data.websites, selectedRegion, selectedCountry, selectedTag, showOnlyUnchecked]);

    const regions = useMemo(() => {
        const r = new Set(data.websites?.map(w => w.Region).filter(Boolean) || []);
        return ['all', ...Array.from(r).sort()];
    }, [data.websites]);

    const countries = useMemo(() => {
        const filtered = data.websites?.filter(w => selectedRegion === 'all' || w.Region === selectedRegion) || [];
        const c = new Set(filtered.map(w => w.Country).filter(Boolean) || []);
        return ['all', ...Array.from(c).sort()];
    }, [data.websites, selectedRegion]);

    const tags = useMemo(() => {
        const t = new Set();
        data.websites?.forEach(w => w.Tags?.forEach(tag => t.add(tag)));
        return ['all', ...Array.from(t).sort()];
    }, [data.websites]);

    const inFlightRequests = useRef(new Set());

    const fetchPatchIntelligence = async () => {
        if (!patchingMode) return;
        
        const cellsToProcess = [];
        filteredWebsites.forEach(site => {
            weekDays.forEach(day => {
                if (!day.isAfter(moment.utc())) {
                    const dateStr = day.format('YYYY-MM-DD');
                    const capture = site.dailyStatus[dateStr];
                    cellsToProcess.push({ site, day, dateStr, capture });
                }
            });
        });

        // Only fetch for cells we don't have intel for yet AND are not currently in-flight
        const cellsToFetch = cellsToProcess.filter(c => {
            const key = `${c.site.WebsitesDBID}_${c.dateStr}`;
            return !patchingIntelligence[key] && !inFlightRequests.current.has(key);
        });

        if (cellsToFetch.length === 0) {
            return;
        }

        // Mark as in-flight
        cellsToFetch.forEach(c => inFlightRequests.current.add(`${c.site.WebsitesDBID}_${c.dateStr}`));

        // Process in batches
        for (let i = 0; i < cellsToFetch.length; i += 10) {
            const batch = cellsToFetch.slice(i, i + 10);
            const results = await Promise.all(batch.map(async ({ site, dateStr, capture }) => {
                const key = `${site.WebsitesDBID}_${dateStr}`;
                try {
                    const params = { websitesDbId: site.WebsitesDBID, targetDate: dateStr };
                    if (capture?.id) {
                        params.targetScanId = capture.id;
                    }
                    const response = await axios.get(`${process.env.REACT_APP_API_ANALYST_URL}/audit/patch-intelligence`, {
                        params,
                        headers: { 'x-api-key': process.env.REACT_APP_API_ANALYST_KEY }
                    });
                    return { key, data: response.data };
                } catch (err) {
                    console.error('Failed to fetch intel for', site.WebsiteName, dateStr);
                    // Return an empty marker to stop the loading spinner
                    return { key, data: { targetTileCount: 0, recommendation: null, candidates: [], error: true } };
                }
            }));

            setPatchingIntelligence(prev => {
                const next = { ...prev };
                results.forEach(r => { 
                    next[r.key] = r.data; 
                    inFlightRequests.current.delete(r.key); // remove from in-flight tracker
                });
                return next;
            });
        }
    };


    useEffect(() => {
        if (patchingMode) {
            fetchPatchIntelligence();
        }
    }, [patchingMode, filteredWebsites, weekDays, refreshTrigger]);

    const getSignedUrl = async (s3Url) => {
        if (!s3Url) return '';
        try {
            const key = s3Url.replace('s3://', '').split('/').slice(1).join('/');
            const apiUrl = `${process.env.REACT_APP_API_URL}/screenshotssigned/${encodeURIComponent(key)}`;
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'x-api-key': process.env.REACT_APP_API_KEY,
                },
            });
            if (!response.ok) return '';
            return await response.text();
        } catch (error) {
            console.error('Error fetching signed URL:', error);
            return '';
        }
    };

    useEffect(() => {
        const updatePreviews = async () => {
            if (selectedPatchCandidate?.imageLocation) {
                const url = await getSignedUrl(selectedPatchCandidate.imageLocation);
                setPatchPreviewUrl(url);
            } else {
                setPatchPreviewUrl('');
            }

            const targetImageLoc = selectedPatch?.site?.dailyStatus[selectedPatch?.dateStr]?.ImageLocation;
            if (targetImageLoc) {
                const url = await getSignedUrl(targetImageLoc);
                setTargetPreviewUrl(url);
            } else {
                setTargetPreviewUrl('');
            }
        };
        updatePreviews();
    }, [selectedPatch, selectedPatchCandidate]);

    const tmChannel = useMemo(() => new BroadcastChannel('sao_paulo_time_machine'), []);

    const handleDeepLink = (site, day) => {
        const dateStr = day.format('YYYY-MM-DD');
        const capture = site.dailyStatus[dateStr];
        const scanId = capture?.id;
        const websiteId = site.WebsitesDBID;
        
        let url = '';
        const payload = { websiteId, date: dateStr };
        
        if (scanId) {
            url = `/time-machine?scanId=${scanId}&websiteId=${websiteId}&date=${dateStr}`;
            payload.scanId = scanId;
        } else {
            url = `/time-machine?websiteId=${websiteId}&date=${dateStr}`;
        }

        // --- Seamless Logic ---
        // 1. Open or reference the named window immediately (synchronous to stay in user gesture)
        const tmWindow = window.open('', 'TimeMachineTab');
        
        let isAlreadyOpen = false;
        try {
            // If we can access the location and it's our page, we just update it
            if (tmWindow.location.pathname.includes('time-machine')) {
                isAlreadyOpen = true;
            }
        } catch (e) {
            // Security error or about:blank means it's not initialized
            isAlreadyOpen = false;
        }

        if (isAlreadyOpen) {
            // Tab is already open and ready, send the data update
            tmChannel.postMessage({ type: 'LOAD_SCAN', ...payload });
        } else {
            // Tab is new or on a different page, perform full navigation
            tmWindow.location.href = url;
        }
        
        if (tmWindow) tmWindow.focus();
    };

    const handleOpenPatchModal = (site, day, intel) => {
        setSelectedPatch({
            site,
            day,
            dateStr: day.format('YYYY-MM-DD'),
            intel: intel.recommendation,
            candidates: intel.candidates
        });
        setSelectedPatchCandidate(intel.recommendation);
        setPatchModalOpen(true);
    };

    const handleExecutePatch = async () => {
        if (!selectedPatch || !selectedPatchCandidate || executingPatch) return;
        
        setExecutingPatch(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_ANALYST_URL}/audit/patch`, {
                sourceScanId: selectedPatchCandidate.scanId,
                targetScanId: selectedPatch.site.dailyStatus[selectedPatch.dateStr]?.id,
                targetWebsitesDbId: selectedPatch.site.WebsitesDBID,
                targetDate: selectedPatch.dateStr
            }, {
                headers: { 'x-api-key': process.env.REACT_APP_API_ANALYST_KEY }
            });
            
            // Refresh summary
            await fetchAuditSummary();
            
            // Clear intelligence for this cell so it refetches immediately
            setPatchingIntelligence(prev => {
                const next = { ...prev };
                delete next[`${selectedPatch.site.WebsitesDBID}_${selectedPatch.dateStr}`];
                return next;
            });
            
            setPatchModalOpen(false);
        } catch (error) {
            console.error('Patch execution failed:', error);
            alert('Failed to execute patch. Check console for details.');
        } finally {
            setExecutingPatch(false);
        }
    };

    const rowAverages = useMemo(() => {
        if (!patchingMode) return {};
        const averages = {};
        filteredWebsites.forEach(site => {
            let total = 0;
            let count = 0;
            weekDays.forEach(day => {
                const key = `${site.WebsitesDBID}_${day.format('YYYY-MM-DD')}`;
                const intel = patchingIntelligence[key];
                // Use only the game count for calculating the average as requested
                const gameCount = intel?.targetCounts?.game ?? intel?.targetTileCount;
                if (intel && gameCount !== undefined) {
                    total += gameCount;
                    count++;
                }
            });
            if (count > 0) {
                averages[site.WebsitesDBID] = total / count;
            }
        });
        return averages;
    }, [patchingMode, filteredWebsites, weekDays, patchingIntelligence]);

    const getStatusIcon = (site, day) => {
        const dateStr = day.format('YYYY-MM-DD');
        const capture = site.dailyStatus[dateStr];
        const isFuture = day.isAfter(moment.utc());
        const intelKey = `${site.WebsitesDBID}_${dateStr}`;
        const intel = patchingIntelligence[intelKey];

        if (capture?.ScreenshotStatus === 'PENDING_MANUAL' || capture?.ScreenshotStatus === 'PROCESSING') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <Tooltip title={`Manual Upload Pending Processing (Scan time: ${moment.utc(capture.Timestamp).format('HH:mm')} UTC)`}>
                        <InboxIcon 
                            sx={{ color: '#ff9800', cursor: 'pointer' }} 
                            onClick={() => handleDeepLink(site, day)} 
                        />
                    </Tooltip>
                    {capture.hasMultiple && (
                        <Tooltip title="Multiple captures found for this day. Click to resolve.">
                            <IconButton 
                                size="small"
                                sx={{ position: 'absolute', right: -12, top: -12, zIndex: 2 }}
                                onClick={(e) => openDuplicateModal(e, site, day)}
                            >
                                <WarningIcon sx={{ color: '#ff9800', fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            );
        }

        if (patchingMode && !isFuture) {
            if (!intel) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 28 }}>
                        <CircularProgress size={16} />
                    </Box>
                );
            }

            const gameCount = intel.targetCounts?.game ?? intel.targetTileCount ?? 0;
            const completeCount = intel.targetCounts?.completeGame ?? gameCount;
            const incompleteCount = intel.targetCounts?.incompleteGame ?? 0;
            const promoCount = intel.targetCounts?.promotion || 0;
            const groupCount = intel.targetCounts?.gameGroup || 0;
            const avg = rowAverages[site.WebsitesDBID];
            
            let color = '#2196f3'; // Default blue
            if (gameCount === 0) {
                color = '#f44336'; // Red
            } else if (avg && gameCount < avg * 0.85) {
                color = '#ff9800'; // Orange
            }            
            return (
                <Tooltip title={`Games: ${gameCount}, Promotions: ${promoCount}, Groups: ${groupCount}. Click for actions.`}>
                    <Box 
                        onClick={(e) => {
                            setActionMenuAnchor(e.currentTarget);
                            setSelectedMenuCell({ site, day, intel: intel || { candidates: [], recommendation: null } });
                        }}
                        sx={{ 
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            minHeight: 32,
                            borderRadius: '4px',
                            border: `1px solid ${color}44`,
                            bgcolor: `${color}08`,
                            mx: 'auto',
                            px: 1,
                            py: 0.5,
                            '&:hover': {
                                bgcolor: `${color}22`,
                                transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        <Typography component="div" sx={{ fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1, display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'center' }}>
                            <span style={{ color: gameCount === 0 ? '#f44336' : '#2196f3' }}>{completeCount}</span>
                            {incompleteCount > 0 && (
                                <>
                                    <span style={{ color: 'rgba(0,0,0,0.3)', fontWeight: 'normal', fontSize: '0.75rem' }}>/</span>
                                    <span style={{ color: '#f44336' }}>{incompleteCount}</span>
                                </>
                            )}
                        </Typography>
                        {(promoCount > 0 || groupCount > 0) && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                                {promoCount > 0 && (
                                    <Typography variant="caption" sx={{ color: '#9c27b0', fontSize: '0.65rem', fontWeight: 'bold', lineHeight: 1 }}>
                                        {promoCount}
                                    </Typography>
                                )}
                                {groupCount > 0 && (
                                    <Typography variant="caption" sx={{ color: '#fbc02d', fontSize: '0.65rem', fontWeight: 'bold', lineHeight: 1 }}>
                                        {groupCount}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Tooltip>
            );
        }

        if (!capture) {
            if (isFuture) {
                return <Box sx={{ width: 24, height: 24, borderRadius: '50%', border: '1px dashed gray', mx: 'auto' }} />;
            }
            
            return (
                <Tooltip title="No Capture Data">
                    <CancelIcon sx={{ color: '#eeeeee', mx: 'auto', display: 'block' }} />
                </Tooltip>
            );
        }

        if (capture.ScreenshotStatus === 'ERROR') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <Tooltip title={`Scan Failed at ${moment.utc(capture.Timestamp).format('HH:mm')} UTC. Click for details or to patch.`}>
                        <CancelIcon 
                            sx={{ color: '#f44336', cursor: 'pointer' }} 
                            onClick={() => handleDeepLink(site, day)} 
                        />
                    </Tooltip>
                    {capture.hasMultiple && (
                        <Tooltip title="Multiple captures found for this day. Click to resolve.">
                            <IconButton 
                                size="small"
                                sx={{ position: 'absolute', right: -12, top: -12, zIndex: 2 }}
                                onClick={(e) => openDuplicateModal(e, site, day)}
                            >
                                <WarningIcon sx={{ color: '#ff9800', fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            );
        }


        const isHighConcern = (capture.BoundingBoxCount !== undefined && capture.BoundingBoxCount > 0 && capture.BoundingBoxCount < 5) || 
                             (capture.MatchedTileCount !== undefined && capture.MatchedTileCount > 0 && capture.MatchedTileCount < 5);

        if (isHighConcern) {
            const concernTooltip = `High Concern: Only ${capture.MatchedTileCount || capture.BoundingBoxCount} tiles found. Manual review required.`;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Tooltip title={concernTooltip}>
                        <WarningIcon 
                            sx={{ color: '#f44336', cursor: 'pointer' }} 
                            onClick={() => handleDeepLink(site, day)} 
                        />
                    </Tooltip>
                </Box>
            );
        }

        const iconColor = capture.Checked ? '#4caf50' : '#2196f3';
        const IconComponent = capture.Checked ? CheckCircleIcon : InboxIcon;
        const tooltipTitle = capture.Checked 
            ? `Checked on ${moment.utc(capture.Timestamp).format('HH:mm')} UTC (${capture.MatchedTileCount || capture.BoundingBoxCount || 0} tiles)`
            : `Captured at ${moment.utc(capture.Timestamp).format('HH:mm')} UTC - Not Checked (${capture.MatchedTileCount || capture.BoundingBoxCount || 0} tiles)`;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <Tooltip title={tooltipTitle}>
                    <IconComponent 
                        sx={{ color: iconColor, cursor: 'pointer' }} 
                        onClick={() => handleDeepLink(site, day)} 
                    />
                </Tooltip>
                {capture.hasMultiple && (
                    <Tooltip title="Multiple captures found for this day. Audit is verified if green, but cleanup may be needed. Click to resolve.">
                        <IconButton 
                            size="small"
                            sx={{ position: 'absolute', right: -12, top: -12, zIndex: 2 }}
                            onClick={(e) => openDuplicateModal(e, site, day)}
                        >
                            <WarningIcon sx={{ color: '#ff9800', fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    };

    // Metrics
    const metrics = useMemo(() => {
        let totalCaptures = 0;
        let checkedCaptures = 0;
        
        filteredWebsites.forEach(site => {
            weekDays.forEach(day => {
                const dateStr = day.format('YYYY-MM-DD');
                const capture = site.dailyStatus[dateStr];
                
                if (capture) {
                    totalCaptures++;
                    if (capture.Checked) {
                        checkedCaptures++;
                    }
                }
            });
        });
        
        const completionRate = totalCaptures > 0 ? (checkedCaptures / totalCaptures) * 100 : 0;
        const pendingCount = totalCaptures - checkedCaptures;
        
        return { 
            totalSites: filteredWebsites.length, 
            totalCaptures,
            checkedCaptures, 
            pendingCount,
            completionRate 
        };
    }, [filteredWebsites, weekDays]);

    return (
        <Box sx={{ py: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="primary" fontSize="large" /> Audit Overview
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button 
                        variant={patchingMode ? "contained" : "outlined"}
                        color={patchingMode ? "secondary" : "primary"}
                        startIcon={<MagicIcon />}
                        onClick={() => setPatchingMode(!patchingMode)}
                        sx={{ mr: 2, borderRadius: 10, textTransform: 'none', fontWeight: 'bold' }}
                    >
                        {patchingMode ? "Patching Mode: ON" : "Enter Patching Mode"}
                    </Button>
                    <Tooltip title="Refresh Audit Data">
                        <IconButton onClick={handleRefresh} color="primary" disabled={loading} sx={{ mr: 1, bgcolor: '#e3f2fd' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <IconButton onClick={() => handleShiftDays(-7)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <TextField
                        type="date"
                        label="Start Date (UTC)"
                        value={startDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        label="End Date (UTC)"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: startDate }}
                    />
                    <IconButton onClick={() => handleShiftDays(7)}>
                        <ArrowForwardIcon />
                    </IconButton>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)', color: 'white' }}>
                        <CardContent>
                            <Typography variant="h6">Audit Coverage</Typography>
                            <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                                {metrics.completionRate.toFixed(1)}%
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={metrics.completionRate} 
                                sx={{ height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} 
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" color="textSecondary">Sites Filtered</Typography>
                            <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>{metrics.totalSites}</Typography>
                            <Typography variant="body2" color="textSecondary">Active websites in current view</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" color="textSecondary">Pending Audit</Typography>
                            <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold', color: metrics.pendingCount > 0 ? 'error.main' : 'success.main' }}>
                                {metrics.pendingCount}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Individual captures awaiting review</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', bgcolor: '#f5f5f5' }}>
                <FilterIcon color="action" />
                <Autocomplete
                    options={regions}
                    size="small"
                    sx={{ width: 150 }}
                    value={selectedRegion}
                    onChange={(_, val) => { setSelectedRegion(val || 'all'); setSelectedCountry('all'); }}
                    renderInput={(params) => <TextField {...params} label="Region" />}
                />
                <Autocomplete
                    options={countries}
                    size="small"
                    sx={{ width: 150 }}
                    value={selectedCountry}
                    onChange={(_, val) => setSelectedCountry(val || 'all')}
                    renderInput={(params) => <TextField {...params} label="Country" />}
                />
                <Autocomplete
                    options={tags}
                    size="small"
                    sx={{ width: 150 }}
                    value={selectedTag}
                    onChange={(_, val) => setSelectedTag(val || 'all')}
                    renderInput={(params) => <TextField {...params} label="Tag" />}
                />
                <Tooltip title="Filter to show only websites with at least one unchecked capture in the current range.">
                    <FormControlLabel
                        control={<Switch checked={showOnlyUnchecked} onChange={(e) => setShowOnlyUnchecked(e.target.checked)} />}
                        label="Show Pending Only"
                    />
                </Tooltip>
                {loading && <CircularProgress size={24} />}
            </Paper>

            <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#eeeeee' }}>Website</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#eeeeee' }}>Location</TableCell>
                            {weekDays.map((day, idx) => (
                                <TableCell key={`head-${idx}-${day.isValid() ? day.format('YYYY-MM-DD') : idx}`} align="center" sx={{ fontWeight: 'bold', bgcolor: '#eeeeee', minWidth: 80 }}>
                                    {day.isValid() ? day.format('ddd') : '---'}<br />
                                    <Typography variant="caption">{day.isValid() ? day.format('DD MMM') : ''}</Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredWebsites.map((site) => (
                            <React.Fragment key={site.WebsitesDBID}>
                                <TableRow hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{site.WebsiteName}</Typography>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => toggleRow(site.WebsitesDBID)}
                                                sx={{ fontSize: '0.7rem', py: 0, minWidth: 'auto', textTransform: 'none' }}
                                            >
                                                {expandedRows[site.WebsitesDBID] ? 'Hide Screens' : 'Show Screens'}
                                            </Button>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                            {site.Tags?.map(tag => (
                                                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">{site.Region}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{site.Country}</Typography>
                                    </TableCell>
                                    {weekDays.map((day, idx) => (
                                        <TableCell key={`${site.WebsitesDBID}-${idx}-${day.isValid() ? day.format('YYYY-MM-DD') : idx}`} align="center">
                                            {day.isValid() ? getStatusIcon(site, day) : null}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                {expandedRows[site.WebsitesDBID] && (
                                    <TableRow sx={{ bgcolor: '#fbfbfb' }}>
                                        <TableCell colSpan={2} sx={{ p: 0.5 }} />
                                        <WebsiteScreensPreview 
                                            site={site} 
                                            weekDays={weekDays} 
                                            getSignedUrl={getSignedUrl} 
                                        />
                                    </TableRow>
                                )}
                            </React.Fragment>
                        ))}
                        {filteredWebsites.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={weekDays.length + 2} align="center" sx={{ py: 10 }}>
                                    <Typography variant="h6" color="textSecondary">No websites found matching your filters.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Patching Modal */}
            <Dialog 
                open={patchModalOpen} 
                onClose={() => !executingPatch && setPatchModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MagicIcon color="secondary" />
                        <Typography variant="h6">Magic Patcher Intelligence</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Target: {selectedPatch?.site?.WebsiteName} | {selectedPatch?.day?.format('DD MMM YYYY')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {selectedPatch && selectedPatchCandidate && (
                        <Box>
                            <Box sx={{ p: 3, bgcolor: '#fdfdfd' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {selectedPatch.site.WebsiteName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Identifying best available successful capture from history to patch missing data.
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ p: 3 }}>
                                <Grid container spacing={4} alignItems="center">
                                    {/* SOURCE COLUMN */}
                                    <Grid item xs={12} md={5}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="overline" color="textSecondary" sx={{ mb: 1, display: 'block' }}>Proposing Replacement</Typography>
                                                <Autocomplete
                                                    size="small"
                                                    options={selectedPatch.candidates || []}
                                                    getOptionLabel={(option) => moment.utc(option.timestamp).format('DD MMM YYYY (HH:mm)')}
                                                    value={selectedPatchCandidate}
                                                    onChange={(event, newValue) => {
                                                        if (newValue) setSelectedPatchCandidate(newValue);
                                                    }}
                                                    renderInput={(params) => <TextField {...params} label="Select Source Date" />}
                                                    disableClearable
                                                />
                                            </Box>
                                            
                                            <Box 
                                                sx={{ 
                                                    aspectRatio: '9/16', 
                                                    bgcolor: '#000', 
                                                    borderRadius: 1, 
                                                    overflow: 'hidden',
                                                    position: 'relative', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                                    mb: 2
                                                }}
                                            >
                                                {patchPreviewUrl ? (
                                                    <img 
                                                        src={patchPreviewUrl} 
                                                        alt="Source Preview" 
                                                        style={{ width: '100%', height: '100%', objectPosition: 'top', objectFit: 'cover' }} 
                                                    />
                                                ) : (
                                                    <CircularProgress color="inherit" />
                                                )}
                                            </Box>

                                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="textSecondary">Games</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{selectedPatchCandidate.gameCount ?? selectedPatchCandidate.tileCount}</Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" sx={{ color: '#9c27b0' }}>Promos</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>{selectedPatchCandidate.promotionCount || 0}</Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" sx={{ color: '#fbc02d' }}>Groups</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#fbc02d' }}>{selectedPatchCandidate.gameGroupCount || 0}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Divider sx={{ my: 1 }} />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">Total Tiles</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{selectedPatchCandidate.tileCount}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">Unassigned</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{selectedPatchCandidate.unassignedTiles}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        </Stack>
                                    </Grid>

                                    {/* ARROW */}
                                    <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                                        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center' }}>
                                            <MagicIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                                            <ArrowForwardIcon sx={{ fontSize: 40, color: 'divider' }} />
                                        </Box>
                                        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', my: 2 }}>
                                            <ArrowBackIcon sx={{ transform: 'rotate(-90deg)', fontSize: 40, color: 'divider' }} />
                                        </Box>
                                    </Grid>

                                    {/* TARGET COLUMN */}
                                    <Grid item xs={12} md={5}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="overline" color="textSecondary" sx={{ mb: 1, display: 'block' }}>Target Destination</Typography>
                                                <TextField 
                                                    size="small" 
                                                    fullWidth 
                                                    value={moment.utc(selectedPatch.dateStr).format('DD MMM YYYY')} 
                                                    disabled 
                                                    label="Target Date"
                                                />
                                            </Box>

                                            <Box 
                                                sx={{ 
                                                    aspectRatio: '9/16', 
                                                    bgcolor: '#f0f0f0', 
                                                    borderRadius: 1, 
                                                    overflow: 'hidden', 
                                                    position: 'relative', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    border: '2px dashed #ccc',
                                                    mb: 2
                                                }}
                                            >
                                                {targetPreviewUrl ? (
                                                    <img 
                                                        src={targetPreviewUrl} 
                                                        alt="Target Preview" 
                                                        style={{ width: '100%', height: '100%', objectPosition: 'top', objectFit: 'cover', opacity: 0.6 }} 
                                                    />
                                                ) : (
                                                    <Stack alignItems="center" spacing={1} color="textSecondary">
                                                        <WarningIcon sx={{ fontSize: 40 }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>No Screenshot Taken</Typography>
                                                    </Stack>
                                                )}
                                                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.05)', pointerEvents: 'none' }} />
                                            </Box>

                                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#eee', opacity: 0.7 }}>
                                                <Typography variant="caption" color="textSecondary" display="block">Current Status</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    Missing / Failed Capture
                                                </Typography>
                                                <Typography variant="caption" color="error">
                                                    Will be overwritten with source data
                                                </Typography>
                                            </Paper>
                                        </Stack>
                                    </Grid>
                                </Grid>
                                {selectedPatchCandidate && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f0f4f8', borderRadius: 2 }}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <strong>Logic:</strong> 
                                            This capture has {selectedPatchCandidate.tileCount} total boxes and is {selectedPatchCandidate.delta} tiles from historical max.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Button onClick={() => setPatchModalOpen(false)} disabled={executingPatch}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        startIcon={executingPatch ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
                        onClick={handleExecutePatch}
                        disabled={executingPatch}
                    >
                        {executingPatch ? "Patching..." : "Execute Magic Patch"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cell Action Menu (in Patching Mode) */}
            <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={() => setActionMenuAnchor(null)}
                PaperProps={{
                    elevation: 3,
                    sx: { minWidth: 200, borderRadius: 2 }
                }}
            >
                <MenuItem 
                    onClick={() => {
                        if (selectedMenuCell) {
                            handleOpenPatchModal(selectedMenuCell.site, selectedMenuCell.day, selectedMenuCell.intel);
                        }
                        setActionMenuAnchor(null);
                    }}
                    sx={{ py: 1 }}
                >
                    <MagicIcon sx={{ mr: 1.5, color: 'secondary.main' }} fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Open Magic Patcher</Typography>
                </MenuItem>
                <MenuItem 
                    onClick={() => {
                        if (selectedMenuCell) {
                            handleDeepLink(selectedMenuCell.site, selectedMenuCell.day);
                        }
                        setActionMenuAnchor(null);
                    }}
                    sx={{ py: 1 }}
                >
                    <InboxIcon sx={{ mr: 1.5, color: 'primary.main' }} fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Open In Time Machine</Typography>
                </MenuItem>
            </Menu>

            {/* What's New Dialog */}
            <Dialog 
                open={whatsNewOpen} 
                onClose={handleCloseWhatsNew}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>✨</span> {whatsNewLang === 'en' ? "What's New in Audit Overview!" : "Novidades no Audit Overview!"}
                    </Typography>
                    <ToggleButtonGroup
                        value={whatsNewLang}
                        exclusive
                        onChange={(e, val) => val && setWhatsNewLang(val)}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                py: 0.2,
                                px: 1.5,
                                fontSize: '1.2rem',
                                borderRadius: '8px',
                            }
                        }}
                    >
                        <ToggleButton value="en" title="English">🇬🇧</ToggleButton>
                        <ToggleButton value="pt" title="Português (Brasil)">🇧🇷</ToggleButton>
                    </ToggleButtonGroup>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        {whatsNewLang === 'en' 
                            ? "We've added some exciting new visual inspection and navigation features to streamline your auditing workflow!"
                            : "Adicionamos novos recursos interessantes de inspeção visual e navegação para otimizar seu fluxo de trabalho de auditoria!"
                        }
                    </Typography>
                    
                    <Stack spacing={2.5}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ fontSize: '1.75rem', bgcolor: 'rgba(25, 118, 210, 0.1)', p: 1, borderRadius: 2, lineHeight: 1 }}>
                                📸
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    {whatsNewLang === 'en' ? "Show Screens Collapsible Preview" : "Prévia Recolhível de Telas"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {whatsNewLang === 'en' 
                                        ? <>Perform quick visual inspections right inside the website table! Clicking the new <strong>Show Screens</strong> button toggles a widescreen screenshot thumbnail gallery for that row.</>
                                        : <>Faça inspeções visuais rápidas diretamente na tabela! Clicar no novo botão <strong>Mostrar Telas</strong> abre uma galeria de miniaturas da captura de tela para essa linha.</>
                                    }
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ fontSize: '1.75rem', bgcolor: 'rgba(156, 39, 176, 0.1)', p: 1, borderRadius: 2, lineHeight: 1 }}>
                                🔗
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    {whatsNewLang === 'en' ? "Going to Time Machine" : "Ir para o Time Machine"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {whatsNewLang === 'en' 
                                        ? "Going to Time Machine from the Audit page now takes you to the right date."
                                        : "Ir para o Time Machine a partir da página de Auditoria agora leva você à data correta."
                                    }
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ fontSize: '1.75rem', bgcolor: 'rgba(76, 175, 80, 0.1)', p: 1, borderRadius: 2, lineHeight: 1 }}>
                                💡
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    {whatsNewLang === 'en' ? "Pending Filters Guidance" : "Orientação sobre Filtros Pendentes"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {whatsNewLang === 'en' 
                                        ? <>Hover over the <strong>Show Pending Only</strong> control to see a detailed tooltip explaining exactly how pending scan items are determined.</>
                                        : <>Passe o mouse sobre o controle <strong>Mostrar Apenas Pendentes</strong> para ver uma dica detalhada explicando exatamente como os itens pendentes são determinados.</>
                                    }
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button 
                        onClick={handleDontShowWhatsNew} 
                        sx={{ textTransform: 'none', color: '#666' }}
                    >
                        {whatsNewLang === 'en' ? "Exit, and Don't Show Again" : "Sair e Não Mostrar Novamente"}
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleCloseWhatsNew}
                        sx={{ px: 3, borderRadius: 2 }}
                    >
                        {whatsNewLang === 'en' ? "Exit" : "Sair"}
                    </Button>
                </DialogActions>
            </Dialog>

            <DuplicateResolutionModal
                open={duplicateModalOpen}
                onClose={() => setDuplicateModalOpen(false)}
                site={duplicateModalTarget.site}
                date={duplicateModalTarget.date}
                onResolutionComplete={handleRefresh}
                getSignedUrl={getSignedUrl}
                apiUrl={process.env.REACT_APP_API_URL}
                apiKey={process.env.REACT_APP_API_KEY}
                analystUrl={process.env.REACT_APP_API_ANALYST_URL}
                analystKey={process.env.REACT_APP_API_ANALYST_KEY}
            />
        </Box>
    );
};

export default AuditOverview;
